import { FilterFilled, SearchOutlined } from "@ant-design/icons";
import { Collapse, Typography } from "antd";
import { useEffect, useState, useContext } from "react";
import CustomFormItemsHorizontal from "../common/CustomFormItemsHorizontal";
import useFetch from "../../hooks/useFetch";
import { IndexContext } from "../../context/IndexContext";
import dayjs from "dayjs";

const { Text } = Typography;

export default function TrendFilter({ form, applyFilter, resetFilter }) {
  const [activeKey, setActiveKey] = useState(1);

  const [fetchWrapper] = useFetch();

  // loading for device list selection (load while it's still getting device list from backend)
  const [loadingDeviceList, setLoadingDeviceList] = useState(false);

  const deviceViewList = [
    { value: 1, label: "Area" },
    { value: 2, label: "SEU" },
    { value: 3, label: "Device" },
  ];
  const [deviceViewSelected, setDeviceViewSelected] = useState(null);

  // device list and its selection
  const [deviceList, setDeviceList] = useState([]);
  const [deviceSelected, setDeviceSelected] = useState(null);
  const [start, setStart] = useState(dayjs().startOf("day"));
  const [end, setEnd] = useState(dayjs().endOf("day"));

  // site commissioning date
  const [siteCommissioningDate, setSiteCommissioningDate] = useState(dayjs());

  // load index context
  const { siteSelected } = useContext(IndexContext);

  /* API to get device list */
  function getDeviceList() {
    setDeviceList([]);
    setLoadingDeviceList(true);
    fetchWrapper({
      endpoint_url: "trending/getDeviceList",
      body: {
        site_id: siteSelected,
        device_view_id: deviceViewSelected,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setDeviceList(fetch_output.device_list);
        }
        setLoadingDeviceList(false);
      },
    });
  }

  /* API to get site commissioning date */
  function getSiteCommissioningDate() {
    fetchWrapper({
      endpoint_url: "trending/getSiteCommissioningDate",
      body: {
        site_id: siteSelected,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setSiteCommissioningDate(fetch_output.site_commissioning_date);
        }
      },
    });
  }

  useEffect(() => {
    if (deviceViewSelected && siteSelected) {
      getDeviceList();
    }
  }, [siteSelected, deviceViewSelected]);

  useEffect(() => {
    if (siteSelected) {
      getSiteCommissioningDate();
    }

    form.setFieldValue("start", start);
    form.setFieldValue("end", end);

    setDeviceViewSelected(null);
    form.setFieldValue("device_view", null);
    setDeviceSelected(null);
    form.setFieldValue("device", []);
  }, [siteSelected]);

  const items = [
    {
      key: 1,
      label: (
        <>
          <FilterFilled />
          <Text strong className="ml-2">
            FILTER
          </Text>
        </>
      ),
      children: (
        <>
          <CustomFormItemsHorizontal
            submitIcon={<SearchOutlined />}
            submitText={"Search"}
            onFinish={applyFilter}
            resetFilter={resetFilter}
            form={form}
            config={[
              {
                name: "start",
                label: "From",
                type: "datetime",
                minDate: dayjs(siteCommissioningDate, "YYYY-MM-DD"),
                maxDate: end ?? dayjs(),
                value: start,
                onChange: setStart,
                rules: [
                  {
                    required: true,
                    message: "Please select start date and time.",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue("end")) {
                        if (getFieldValue("end") < value) {
                          return Promise.reject(
                            new Error(
                              "Start date must be same as or earlier than end date."
                            )
                          );
                        } else {
                          return Promise.resolve();
                        }
                      }
                    },
                  }),
                ],
              },
              {
                name: "end",
                label: "To",
                type: "datetime",
                maxDate: dayjs(),
                minDate: start ?? dayjs(siteCommissioningDate, "YYYY-MM-DD"),
                value: end,
                onChange: setEnd,
                rules: [
                  {
                    required: true,
                    message: "Please select end date and time.",
                  },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (getFieldValue("start")) {
                        if (getFieldValue("start") > value) {
                          return Promise.reject(
                            new Error(
                              "End date must be same as or later than start date."
                            )
                          );
                        } else {
                          return Promise.resolve();
                        }
                      }
                    },
                  }),
                ],
              },
              {
                name: "device_view",
                label: "View Device By",
                type: "select",
                options: deviceViewList,
                onChange: (value) => {
                  setDeviceViewSelected(value);
                  form.setFieldValue("device", []);
                },
                rules: [
                  {
                    required: true,
                    message: "Please select how do you want to view device.",
                  },
                ],
              },
              {
                name: "device",
                label: "Device",
                type: "tree",
                multiple: true,
                tooltip: deviceViewSelected
                  ? deviceList.length === 0
                    ? "There is no device for the selected view."
                    : "'*' means the device is the source of area / SEU. \
                    If you select area / SEU, the sum of the devices will be calculated. \
                    If the area / SEU selection is disabled, it means none of the devices is the source for the area / SEU."
                  : "Please select how to you want to view device.",
                onChange: (value) => {
                  setDeviceSelected(value);
                },
                rules: [{ required: true, message: "Please select device." }],
                showCheckedStrategy: "all",
                options: deviceList,
                disabled: siteSelected && deviceViewSelected ? false : true,
                loading: loadingDeviceList,
              },
            ]}
          />
        </>
      ),
    },
  ];

  return (
    <Collapse
      expandIconPosition="end"
      defaultActiveKey={activeKey}
      size="middle"
      onChange={(key) => setActiveKey(key)}
      items={items}
    ></Collapse>
  );
}
